// Roving focus (DS §2.1 Focus) for a radiogroup, toolbar, tablist or menu built from non-native items. The caller
// gives the container its role and aria-labelledby and spreads `container` on it; each item spreads item(i). One item
// is in the tab order (the active one, else the first enabled); arrows move with wrap, Home/End jump; with
// selectOnFocus (radios) the move selects; Escape calls onEscape (a menu closes and its opener takes focus back).
// When focus leaves the group, the tab stop returns to the active item if there is one (a radiogroup arrowed onto a
// blocked, unchosen option is re-entered on its chosen one); with no active item (a menu, a toolbar) it stays put. A
// slot may stay empty (a row's gap): the key handler skips it.
// A group of native same-name <input type=radio> already has one tab stop: it does not use this hook.

import { useCallback, useEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type RefObject } from "react";

export interface RovingOptions {
  count: number;
  /** The checked or selected item; it holds the tab stop. -1 or absent: the first enabled item. */
  active?: number | undefined;
  orientation?: "horizontal" | "vertical" | "both" | undefined;
  wrap?: boolean | undefined;
  /** Radios: moving focus selects (onSelect fires). */
  selectOnFocus?: boolean | undefined;
  onSelect?: ((index: number) => void) | undefined;
  /** Menus: Escape closes (the caller hides the menu) ... */
  onEscape?: (() => void) | undefined;
  /** ... and focus returns to the opener. */
  returnFocusTo?: RefObject<HTMLElement | null> | undefined;
  isDisabled?: ((index: number) => boolean) | undefined;
}

export interface RovingItemProps {
  tabIndex: 0 | -1;
  ref: (element: HTMLElement | null) => void;
  onFocus: () => void;
}

export interface Roving {
  container: { onKeyDown: (event: KeyboardEvent<HTMLElement>) => void; onBlur: (event: FocusEvent<HTMLElement>) => void };
  item: (index: number) => RovingItemProps;
  /** Moves focus (and the tab stop) to an item. */
  focus: (index: number) => void;
  /** The item that holds the tab stop. */
  current: number;
}

const NEXT: Record<string, ReadonlyArray<"horizontal" | "vertical" | "both">> = {
  ArrowRight: ["horizontal", "both"], ArrowDown: ["vertical", "both"],
};
const PREV: Record<string, ReadonlyArray<"horizontal" | "vertical" | "both">> = {
  ArrowLeft: ["horizontal", "both"], ArrowUp: ["vertical", "both"],
};

export function useRoving(options: RovingOptions): Roving {
  const { count, active, orientation = "both", wrap = true, selectOnFocus = false, onSelect, onEscape, returnFocusTo, isDisabled } = options;
  const enabled = useCallback((index: number) => index >= 0 && index < count && !(isDisabled?.(index) ?? false), [count, isDisabled]);
  const firstEnabled = useCallback(() => {
    for (let i = 0; i < count; i++) if (enabled(i)) return i;
    return 0;
  }, [count, enabled]);
  const [stop, setStop] = useState(() => (active !== undefined && enabled(active) ? active : firstEnabled()));
  const elements = useRef<(HTMLElement | null)[]>([]);

  // The tab stop follows the selection, and never rests on an item that is gone or disabled.
  useEffect(() => {
    if (active !== undefined && active >= 0 && enabled(active)) setStop(active);
    else setStop((held) => (enabled(held) ? held : firstEnabled()));
  }, [active, enabled, firstEnabled]);

  const focus = useCallback((index: number) => {
    setStop(index);
    elements.current[index]?.focus();
  }, []);

  const step = useCallback((from: number, delta: 1 | -1): number => {
    for (let n = 1; n <= count; n++) {
      let next = from + delta * n;
      if (wrap) next = ((next % count) + count) % count;
      else if (next < 0 || next >= count) return from;
      if (enabled(next)) return next;
    }
    return from;
  }, [count, wrap, enabled]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape" && onEscape) {
      event.preventDefault();
      onEscape();
      returnFocusTo?.current?.focus();
      return;
    }
    const from = elements.current.findIndex((element) => element != null && element.contains(event.target as Node));
    if (from < 0 || count === 0) return;
    let to: number | null = null;
    if (NEXT[event.key]?.includes(orientation)) to = step(from, 1);
    else if (PREV[event.key]?.includes(orientation)) to = step(from, -1);
    else if (event.key === "Home") to = firstEnabled();
    else if (event.key === "End") {
      to = from;
      for (let i = count - 1; i >= 0; i--) if (enabled(i)) { to = i; break; }
    }
    if (to === null) return;
    event.preventDefault();
    focus(to);
    if (selectOnFocus && to !== active) onSelect?.(to);
  };

  const onBlur = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget as Node | null;
    if (next && event.currentTarget.contains(next)) return;
    if (active !== undefined && active >= 0 && enabled(active)) setStop(active);
  };

  return {
    container: { onKeyDown, onBlur },
    item: (index) => ({
      tabIndex: index === stop ? 0 : -1,
      ref: (element) => { elements.current[index] = element; },
      onFocus: () => setStop(index),
    }),
    focus,
    current: stop,
  };
}
